package nl.astraeus.wiki.messages

enum class MessageType(
  val messageType: String,
) {
  EMPTY("Empty"),
  PING("Ping"),
  CREATE_TOKEN("CreateToken"),
  GET_WIKI_LIST("GetWikiList"),
  GET_WIKI("GetWiki"),
  WIKI_LIST("WikiList"),
  CREATE_PAGE("CreatePage"),
  GET_PAGE("GetPage"),
  PAGE_MESSAGE("PageMessage"),
  LOGIN("Login"),
  LOGIN_SUCCESS("LoginSuccess"),
  LOGIN_FAILURE("LoginFailure"),
  LOGOUT("Logout"),
  LOGOUT_SUCCESS("LogoutSuccess"),
  CREATE_WIKI("CreateWiki"),
  WIKI_LIST_ENTRY_MESSAGE("WikiListEntryMessage"),
  FORBIDDEN_MESSAGE("ForbiddenMessage"),
  ERROR_MESSAGE("ErrorMessage"),
  WIKI_MESSAGE("WikiMessage"),
  UPDATE_WIKI_TITLE("UpdateWikiTitle"),
  NOT_FOUND_MESSAGE("NotFoundMessage"),
  SAVE_PAGE("SavePageMessage"),
  REMOVE_PAGE("RemovePageMessage"),
  UPDATE_PAGE_TITLE("UpdatePageTitle"),
  UPDATE_PAGE_ORDER("UpdatePageOrder"),
  SUCCESS("SuccessMessage"),
  OAUTH_INFO("OauthInfo"),
  OAUTH_INFO_RESPONSE("OauthInfoResponse"),
  GOOGLE_LOGIN("GoogleLogin"),
  GOOGLE_LOGIN_RESPONSE("GoogleLoginResponse"),
  UPDATE_WIKI_ORDER("UpdateWikiOrder"),
  REMOVE_WIKI("RemoveWiki"),
  ;
}

enum class MessageHeader(
  val headerName: String,
) {
  MESSAGE_TYPE("MessageType"),
  MESSAGE_ID("MessageId"),
  WIKI_ID("WikiId"),
  WIKI_TITLE("WikiTitle"),
  PAGE_ID("PageId"),
  PAGE_TITLE("PageTitle"),
  PAGE_IDS("PageIds"),
  OBJECT_ID("ObjectId"),
  EMAIL("Email"),
  USERNAME("Username"),
  PASSWORD("Password"),
  REASON("Reason"),
  WIKI_IDS("WikiIds"),
  OAUTH_TYPE("OauthType"),
  CLIENT_ID("OauthClientId"),
  CLIENT_SECRET("OauthClientSecret"),
  OAUTH_STATE("OauthState"),
  GOOGLE_CODE("GoogleCode"),
  GOOGLE_TOKEN("GoogleToken"),
  RESULT("Result"),
  USER_NAME("UserName"),
  USER_EMAIL("UserEmail"),
}

open class Message(
  type: MessageType,
  vararg headers: MessageHeader,
) {
  val headers = arrayOf(
    MessageHeader.MESSAGE_TYPE,
    MessageHeader.MESSAGE_ID,
    *headers
  )
  var message: String = createEmptyMessage()
    set(value) {
      val lines = value.lines()

      if (lines.size < headers.size) {
        error("Message to small for this type of message: ${this::class.simpleName}, only ${lines.size} lines")
      }

      for ((index, line) in lines.withIndex()) {
        if (index < headers.size) {
          check(line.startsWith(headers[index].headerName)) {
            "Invalid header for message type ${this::class.simpleName}: $line, expected ${headers[index].headerName}"
          }
        }
      }

      field = value
    }

  init {
    setHeader(MessageHeader.MESSAGE_TYPE, type.name)
  }

  constructor(
    msg: String
  ) : this(MessageType.EMPTY) {
    this.message = msg
  }

  fun checkType(
    messageType: MessageType,
  ) {
    check(this.getHeader(MessageHeader.MESSAGE_TYPE) == messageType.name)
  }

  fun getMessageType(): MessageType {
    return MessageType.valueOf(getHeader(MessageHeader.MESSAGE_TYPE))
  }

  fun getHeader(header: MessageHeader): String {
    val lines = message.lines()

    for ((index, messageHeader) in headers.withIndex()) {
      if (header == messageHeader && lines.size > index) {
        return lines[index].substring(header.headerName.length + 1).trim()
      }
    }

    throw IllegalArgumentException("Header $header not found in this message: $message")
  }

  fun setHeader(header: MessageHeader, value: String) {
    val lines = message.lines()
    val result = StringBuilder()

    for ((index, messageHeader) in headers.withIndex()) {
      if (lines.size > index) {
        if (header == messageHeader && lines.size > index) {
          result.append(header.headerName)
          result.append(": ")
          result.append(value)
        } else {
          result.append(lines[index])
        }
        result.append("\n")
      } else {
        result.append(header.headerName)
        result.append(": \n")
      }
    }

    result.append("\n")
    result.append(getBody())

    message = result.toString()
  }

  fun setBody(value: String) {
    val lines = message.lines()
    val result = StringBuilder()

    repeat(headers.size+1) { index ->
      if (lines.size > index) {
        result.append(lines[index])
        result.append("\n")
      } else {
        result.append("\n")
      }
    }

    result.append(value)

    message = result.toString()
  }

  fun getBody(): String {
    val lines = message.lines()
    val response = StringBuilder()

    for ((index, line) in lines.withIndex()) {
      if (index > headers.size) {
        response.append(line)
        response.append("\n")
      }
    }

    return response.toString()
  }

  private fun createEmptyMessage(): String {
    val result = StringBuilder()

    for (header in headers) {
      result.append(header.headerName)
      result.append(": \n")
    }
    result.append("\n")

    return result.toString()
  }
}
